import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { useAuth } from "../../auth/contexts/AuthContext";
import { useProjects } from "../contexts/ProjectContext";
import { slugify } from "../../../utils/slugify";

/**
 * A compact project-switching dropdown that appears in the header
 * for Superadmin users across ALL modules/layouts.
 *
 * When a project is selected it updates the ProjectContext AND
 * navigates to the equivalent route under the new project slug,
 * preserving the current module (e.g. /old-slug/logger → /new-slug/logger).
 */
export const SuperadminProjectSelector: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { projects, activeProject, selectProject } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectSlug } = useParams();

  // Only render for Superadmin and Admin users
  if (user?.role !== "Superadmin" && user?.role !== "Admin") return null;

  // Don't render if there are no projects yet
  if (projects.length === 0) return null;

  const handleProjectChange = (projectId: string | null) => {
    if (!projectId) return;
    const selected = projects.find((p) => p.id === projectId);
    if (!selected) return;

    selectProject(projectId);

    // Preserve the current module path (e.g., /logger, /editor, /superadmin)
    const newSlug = slugify(selected.name);
    if (projectSlug) {
      // Replace old slug with new slug in the current path
      const currentPath = location.pathname;
      const modulePath = currentPath.replace(`/${projectSlug}`, "");
      navigate(`/${newSlug}${modulePath}`);
    } else {
      navigate(`/${newSlug}`);
    }
  };

  return (
    <Select value={activeProject?.id || ""} onValueChange={handleProjectChange}>
      <SelectTrigger
        data-testid="superadmin-project-selector"
        className="h-8 w-[180px] border-white/10 bg-white/5 text-xs text-muted-foreground hover:bg-white/10 focus:ring-0 focus:ring-offset-0"
      >
        <div className="flex items-center gap-1.5 overflow-hidden">
          <i
            className="bx bx-briefcase-alt-2 shrink-0"
            style={{ fontSize: "0.9rem", color: "var(--color-accent-lime, #A3E635)" }}
          />
          <span className="truncate text-[0.75rem]">
            {activeProject?.name || t("admin.layout.selectProject", "Seleccionar proyecto")}
          </span>
        </div>
      </SelectTrigger>
      <SelectContent className="z-[200]">
        {projects.map((proj) => (
          <SelectItem key={proj.id} value={proj.id}>
            {proj.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
